import { useState, useEffect, useCallback, useRef } from 'react'
import { apiGet, apiPost, apiPut } from '../../../../../generic/Api_Functions'
import useMosInfo from '../../../common/provider/useMosInfo'
import AlertUI from '../../../../../generic/AlertUI'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import { requiresNonProductiveTaskCheck } from '../utils/jobStatus'

/**
 * The confirmations Sun may ask for before it applies a status change. Both travel as plain booleans
 * in the request body, and ONE finish can need BOTH: the multi-device check is a fail-fast pre-check
 * that runs before the auto-consumption, so the incomplete-parts count only becomes meaningful on the
 * next call. Carrying them as one object is what keeps that third request from depending on the order
 * of two positional booleans.
 */
const NO_CONFIRMATIONS = { reportIncompleteAsNok: false, interruptInOtherDevices: false }

const CLOSED_CONFIRM = { open: false, count: 0, statusCode: '', confirmations: NO_CONFIRMATIONS }
const CLOSED_OTHER_DEVICES = { open: false, devices: [], statusCode: '', confirmations: NO_CONFIRMATIONS }

// Sun answers HTTP 200 with this type, instead of applying the change, whenever it needs the operator
// to confirm something first. `reason` discriminates WHICH confirmation: the multi-device one names
// itself, and anything else -- including an older Sun that sends no reason at all -- is the
// incomplete-parts one that shipped first. Defaulting to the older meaning is what keeps this hook
// working against a Sun that has not deployed the multi-device flow yet.
const CONFIRMATION_REQUIRED = 'CONFIRMATION_REQUIRED'
const OTHER_DEVICES_REASON = 'JOB_ACTIVE_IN_OTHER_DEVICES'

const isConfirming = (confirmations) =>
  confirmations.reportIncompleteAsNok || confirmations.interruptInOtherDevices

/**
 * Custom hook to handle job status changes.
 *
 * Direction (b) of the exclusivity rule (RN3b, UI-only): joining/starting a job while the worker
 * has an active non-productive task raises a one-step offer — stop the task (setEnd) and join.
 * The state is resolved fresh against worker/getActiveNonProductiveTasks (the context worker's
 * flag may be stale) and the same call provides the session id for the setEnd (historia #67 / S12).
 *
 * @param {Object} params - Hook parameters
 * @param {boolean} params.open - Whether the dialog is open
 * @param {Function} params.onSuccess - Callback on success
 * @param {Function} params.onCancell - Callback on cancel
 * @param {Object} params.fabricationOrder - Fabrication Order object
 * @param {Object} params.jobReference - Job Reference object
 * @param {boolean} [params.autoReport=false] - Whether the backend should auto-report the part when the status change is applied
 * @param {string} [params.intent=''] Story 113 (S116/S117, R-F): what the operator is DOING, declared
 *        apart from the target status code. Only a start (JOB_ACTION_INTENT.START — which covers
 *        starting, reopening, joining and resuming) checks the open non-productive task; a mode
 *        transition happens mid-job, with the operator already on it, so the stop-and-join offer does
 *        not apply. It cannot be decided from `statusCode`: a preparation -> execution transition
 *        sends EXACTLY the code a plain start sends.
 * @returns {Object} Hook state and functions
 */
const useJobStatusChange = ({ open, onSuccess, onCancell, fabricationOrder, jobReference, autoReport = false, intent = '' }) => {
  const { t_ } = useSmartTranslation()
  const { worker, device } = useMosInfo()
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(false)
  // Two-step NOK confirmation: when finishing a job whose parts still have material to consume, the
  // backend answers HTTP 200 with { type: 'CONFIRMATION_REQUIRED', incompletePartsToReportNok }
  // instead of closing. We surface a confirmation dialog and, on accept, resend the same request
  // with confirmReportIncompleteAsNok=true (same pattern as ModifyQuantityDialog).
  const [incompleteConfirm, setIncompleteConfirm] = useState(CLOSED_CONFIRM)
  // Same two-step shape for a job still running on OTHER machines: Sun answers 200 with
  // { type: 'CONFIRMATION_REQUIRED', reason: 'JOB_ACTIVE_IN_OTHER_DEVICES', activeDevices } and the
  // operator decides whether to close those periods too. Cancelling finishes NOTHING -- the job stays
  // active on every machine, this one included.
  const [otherDevicesConfirm, setOtherDevicesConfirm] = useState(CLOSED_OTHER_DEVICES)
  // S12: pending stop-and-join offer — { task: WorkerOtherTaskDto, statusCode } while the user decides.
  const [nonProductiveConflict, setNonProductiveConflict] = useState(null)
  const [AlertElement, showAlert, dismiss] = AlertUI()
  // A double click on the start button opened TWO rows in `device_fabrication_order` for the same
  // (device, order, job, worker), 80 ms apart, in production. `loading` cannot stop that: it is
  // state, so the second click of the same event batch still reads `false` -- the re-render that
  // would have disabled the buttons has not happened yet. This ref is written SYNCHRONOUSLY on the
  // way in, before any await, which is the only thing the second click can observe.
  const inFlightRef = useRef(false)

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      dismiss()
      setComment('')
      setLoading(false)
      // A dialog closed mid-request would otherwise leave the flag stuck and kill the next attempt.
      inFlightRef.current = false
      setIncompleteConfirm(CLOSED_CONFIRM)
      setOtherDevicesConfirm(CLOSED_OTHER_DEVICES)
      setNonProductiveConflict(null)
    }
  }, [open, dismiss])

  const showStatusChangeError = useCallback((error) => {
    console.error('Error changing job status:', error)
    const errorMessage = Array.isArray(error.data)
      ? error.data.map(msg => t_(msg)).join('. ')
      : t_(error.message || 'Error updating status')
    showAlert({ severity: 'error', title: errorMessage })
  }, [t_, showAlert])

  const applyStatusChange = useCallback(async (statusCode, confirmations = NO_CONFIRMATIONS) => {
    setLoading(true)
    try {
      const status = await apiGet('fabricationOrderStatus/byCode/' + statusCode)

      const data = {
        deviceId: device.id,
        fabricationOrderId: fabricationOrder.id,
        statusId: status.id,
        comment,
        workerId: worker.id,
        jobReferenceId: jobReference.id,
        reason: null,
        check: true,
        autoReport,
        confirmReportIncompleteAsNok: confirmations.reportIncompleteAsNok,
        // Sending this field AT ALL is the opt-in: it tells Sun this client knows how to answer a
        // CONFIRMATION_REQUIRED about other machines. Omitting it is what keeps the old outright
        // rejection for the clients that would read a 200 as success.
        interruptInOtherDevices: confirmations.interruptInOtherDevices
      }

      const response = await apiPut('mos/deviceFabricationOrder/updateStatus', data)

      // The backend needs the operator to confirm something before it applies the change: keep the
      // dialog open and surface the confirmation instead of closing as success. Each confirmation
      // carries the ones already granted, so the request that answers the second one still states the
      // first.
      if (response?.type === CONFIRMATION_REQUIRED) {
        if (response.reason === OTHER_DEVICES_REASON) {
          setOtherDevicesConfirm({
            open: true,
            devices: Array.isArray(response.activeDevices) ? response.activeDevices : [],
            statusCode,
            confirmations
          })
          return
        }
        setIncompleteConfirm({
          open: true,
          count: response.incompletePartsToReportNok ?? 0,
          statusCode,
          confirmations
        })
        return
      }

      setIncompleteConfirm(CLOSED_CONFIRM)
      setOtherDevicesConfirm(CLOSED_OTHER_DEVICES)
      onCancell()
      if (onSuccess) onSuccess()
    } finally {
      setLoading(false)
    }
  }, [device, fabricationOrder, jobReference, worker, comment, autoReport, onCancell, onSuccess])

  const handleChangeStatus = useCallback(async (statusCode, confirmations = NO_CONFIRMATIONS) => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    // Raised HERE and not inside applyStatusChange: on a start, the non-productive-task GET below
    // runs first, and the dialog disables its buttons with `disableButtons={loading}`. Leaving it
    // for later is what kept them clickable during that round-trip -- the exact window the second
    // click came through.
    setLoading(true)
    try {
      if (!fabricationOrder?.id || !jobReference?.id || !device?.id || !worker?.id) {
        throw new Error('Missing required information to change status')
      }

      // S12 (dir. b): starting/joining while the worker has an active non-productive task raises the
      // stop-and-join offer instead of applying the change. Only on START, and not when re-entering
      // after a confirmation (the task, if any, was already resolved on the first pass).
      //
      // Story 113 (S116/S117, R-F): "is this a start?" is now the INTENT, not the target code. It has
      // to be: a preparation -> execution transition sends the very same `ACTIVE_EXECUTION` a start
      // sends, so comparing codes would raise this offer mid-job (S117), and starting in
      // `ACTIVE_PREPARATION` would silently lose the check (S116).
      if (requiresNonProductiveTaskCheck(intent) && !isConfirming(confirmations)) {
        const activeTask = await apiGet('worker/getActiveNonProductiveTasks/' + worker.code)
        if (activeTask && activeTask.endDate === null) {
          setNonProductiveConflict({ task: activeTask, statusCode })
          return
        }
      }

      await applyStatusChange(statusCode, confirmations)
    } catch (error) {
      showStatusChangeError(error)
    } finally {
      // Released even when the call ended by opening a confirmation dialog and returning:
      // `confirmReportIncomplete` and `confirmInterruptOtherDevices` come straight back in here, and
      // a flag left set would make their confirm button dead.
      inFlightRef.current = false
      setLoading(false)
    }
  }, [fabricationOrder, jobReference, device, worker, intent, applyStatusChange, showStatusChangeError])

  // Operator confirmed: resend the same status change, now telling the backend to report the
  // incomplete parts as NOK and finish.
  const confirmReportIncomplete = useCallback(() => {
    const { statusCode, confirmations } = incompleteConfirm
    setIncompleteConfirm(CLOSED_CONFIRM)
    handleChangeStatus(statusCode, { ...confirmations, reportIncompleteAsNok: true })
  }, [incompleteConfirm, handleChangeStatus])

  const cancelIncompleteConfirm = useCallback(() => {
    setIncompleteConfirm(CLOSED_CONFIRM)
  }, [])

  // Operator confirmed: resend the same status change, now telling the backend to close the job's
  // open periods on the other machines too. Sun re-reads them, so the list that was shown is
  // informative -- what actually gets interrupted is whatever is still active at that moment.
  const confirmInterruptOtherDevices = useCallback(() => {
    const { statusCode, confirmations } = otherDevicesConfirm
    setOtherDevicesConfirm(CLOSED_OTHER_DEVICES)
    handleChangeStatus(statusCode, { ...confirmations, interruptInOtherDevices: true })
  }, [otherDevicesConfirm, handleChangeStatus])

  // Nothing was written on the first call, so cancelling just closes the offer: the job stays active
  // everywhere, this machine included.
  const cancelInterruptOtherDevices = useCallback(() => {
    setOtherDevicesConfirm(CLOSED_OTHER_DEVICES)
  }, [])

  // The SECOND user entry point: it reaches applyStatusChange without passing through
  // handleChangeStatus, so it needs the same synchronous guard -- otherwise two clicks on "stop and
  // join" send two setEnd + updateStatus pairs.
  const acceptNonProductiveStop = useCallback(async () => {
    if (inFlightRef.current || !nonProductiveConflict) return
    inFlightRef.current = true
    setLoading(true)
    const { task, statusCode } = nonProductiveConflict
    setNonProductiveConflict(null)
    try {
      await apiPost('mos/setEnd/nonProductiveTask/' + task.id)
      await applyStatusChange(statusCode)
    } catch (error) {
      showStatusChangeError(error)
    } finally {
      inFlightRef.current = false
      setLoading(false)
    }
  }, [nonProductiveConflict, applyStatusChange, showStatusChangeError])

  const cancelNonProductiveStop = useCallback(() => {
    setNonProductiveConflict(null)
  }, [])

  return {
    comment,
    setComment,
    handleChangeStatus,
    loading,
    AlertElement,
    incompleteConfirm,
    confirmReportIncomplete,
    cancelIncompleteConfirm,
    otherDevicesConfirm,
    confirmInterruptOtherDevices,
    cancelInterruptOtherDevices,
    nonProductiveConflict,
    acceptNonProductiveStop,
    cancelNonProductiveStop
  }
}

export default useJobStatusChange
